import { useEffect, useState, startTransition } from "react";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";

let cache = null, pending = null;
export const loadOffers = () => cache ? Promise.resolve(cache) : (pending ||= api.get("/offers").then((r) => (cache = r.data)).catch(() => (cache = [])));
export const resetOffers = () => { cache = null; pending = null; };

/** Aktive Marketing-Angebote (öffentlich, gecacht). */
export const useOffers = () => {
  const [offers, setOffers] = useState(cache || []);
  useEffect(() => { loadOffers().then((o) => startTransition(() => setOffers(o))); }, []);
  return offers;
};

/** Greift das Angebot grundsätzlich für dieses Objekt (Insel / Typ / Objektliste)? Zeitraum/Nächte prüft die Preisberechnung. Bei „nur auf Wunsch“ (opt_in) nie automatisch. */
export const offerInScope = (o, p) => !o.opt_in && (!o.island || p.island === o.island) && (!o.property_type || p.type === o.property_type) && (!o.property_ids?.length || o.property_ids.includes(p.id));
export const offerApplies = (o, p) => !(p.offers_disabled || []).includes(o.id) && ((p.offers_enabled || []).includes(o.id) || offerInScope(o, p));
export const offersFor = (offers, p) => offers.filter((o) => offerApplies(o, p));
export const offerLabel = (o, lang) => o.label?.[lang] || o.label?.de || o.name?.[lang] || o.name?.de || "";

/** Zahlen, die der Vermieter je Objekt selbst festlegt (offer_params[offer_id]). */
export const OFFER_PARAM_KEYS = ["percent", "days", "stay_nights", "pay_nights", "min_nights"];
/** Angebot mit den objekteigenen Zahlen statt der Admin-Standardwerte; unplausible Werte fallen auf den Standard zurück (wie im Backend). */
export const effectiveOffer = (o, p) => {
  const own = (p?.offer_params || {})[o.id] || {};
  const eo = { ...o };
  for (const k of OFFER_PARAM_KEYS) if (own[k] !== undefined && own[k] !== null && own[k] !== "") eo[k] = Number(own[k]);
  if (eo.kind === "free_nights" && !(eo.stay_nights > eo.pay_nights && eo.pay_nights > 0)) { eo.stay_nights = o.stay_nights; eo.pay_nights = o.pay_nights; }
  if (eo.kind !== "free_nights" && !(eo.percent > 0)) eo.percent = o.percent;
  return eo;
};
/** Allgemeiner Kategoriename (Last Minute, Freie Nächte …) – Admin-Titel ohne Zahlen hat Vorrang. */
export const offerKindName = (o, t, lang) => o.name?.[lang] || o.name?.de || t.offers.kinds?.[o.kind] || o.kind;
export const offerGenericHint = (o, t) => t.offers.generic?.[o.kind] || "";
/** Konkrete Konditionen eines Objekts („Last Minute −10 %“, „Freie Nächte: 21 buchen, 17 bezahlen“). */
export const offerDetailLabel = (eo, t, lang) => eo.kind === "free_nights"
  ? `${offerKindName(eo, t, lang)}: ${eo.stay_nights} ${t.offers.book}, ${eo.pay_nights} ${t.offers.pay}`
  : `${offerKindName(eo, t, lang)} −${eo.percent} %`;

/** Kurzer Bedingungstext („bei Anreise innerhalb 14 Tage“, „ab 21 Nächten – 4 Nächte geschenkt“ …). */
export const offerHint = (o, t) => {
  const h = t.offers.hints;
  const travel = (o.travel_from || o.travel_until) ? ` · ${t.offers.travel.replace("{from}", o.travel_from ? fmtDate(o.travel_from) : "…").replace("{until}", o.travel_until ? fmtDate(o.travel_until) : "…")}` : "";
  const until = o.valid_until ? ` · ${t.offers.bookBy.replace("{date}", fmtDate(o.valid_until))}` : "";
  const base = {
    last_minute: h.last_minute.replace("{days}", o.days),
    early_bird: h.early_bird.replace("{days}", o.days),
    long_stay: h.long_stay.replace("{n}", o.min_nights),
    gap: (h.gap || "").replace("{days}", o.days),
    free_nights: h.free_nights.replace("{n}", o.stay_nights).replace("{free}", o.stay_nights - o.pay_nights),
    season: h.season,
  }[o.kind] || "";
  return `${base}${o.kind !== "season" || !travel ? travel : travel.slice(3)}${until}`.replace(/^ · /, "");
};
